import { addDays, parseIso, todayIso, type BudgetPeriod, type Transaction } from '@fin/shared';
import { useEffect, useMemo, useState } from 'react';
import { CategoryPicker, TransactionModal, TxRow, WalletPicker, dedupeTransfers } from './screen-exports.ts';
import { TxDetail } from './detail.tsx';
import { Button, Empty, ErrorBox, Field, Icon, Input, Loading, Modal, PageHead, Tile, cx } from '../../components/ui.tsx';
import { NetworkError, get, qs } from '../../lib/api.ts';
import { categoryLabel, useCategories, useRates, useWallets } from '../../lib/data.ts';
import { dateLong, dateShort, kindLabels, monthName, money, plural, rangeLabel, rub, weekday } from '../../lib/format.ts';
import { categoryColor, categoryIcon, kindTint, walletIcon } from '../../lib/icons.tsx';
import { prefetch, useQuery } from '../../lib/query.ts';
import { Link, setQuery, useLocation } from '../../lib/router.tsx';
import { useSwipe } from '../../lib/swipe.ts';

/** a shared expense counts only the owner's part: other people's uncovered shares are theirs */
const ownPart = (t: Transaction) => t.amount + (t.amount < 0 ? t.sharedAmount ?? 0 : 0);

const PAGE = 100;
type TxPage = { items: Transaction[]; hasMore: boolean };
const firstPage = (key: string) => `/transactions${key}${key ? '&' : '?'}limit=${PAGE}&offset=0`;

// ---------- date ranges ----------
const monthOf = (iso: string) => iso.slice(0, 7);
const monthStart = (ym: string) => `${ym}-01`;
function monthEnd(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
}
function shiftMonth(ym: string, by: number) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
const monthTitle = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `${monthName(m - 1)}${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
};

type Range = { from: string; to: string } | null;

export function TransactionsPage() {
  const { query } = useLocation();
  const today = todayIso();
  // the month is the default range; «all time» is explicit so it survives reloads
  const all = query.get('all') === '1';
  const range: Range = all ? null : query.get('from') && query.get('to')
    ? { from: query.get('from')!, to: query.get('to')! }
    : { from: monthStart(monthOf(today)), to: monthEnd(monthOf(today)) };
  const filters = {
    walletId: query.get('wallet') ?? '',
    categoryId: query.get('category') ?? '',
    from: range?.from ?? '',
    to: range?.to ?? '',
    q: query.get('q') ?? '',
    kind: Object.hasOwn(kindLabels, query.get('kind') ?? '') ? query.get('kind')! : '',
    excluded: query.get('excluded') === '1' ? '1' : '',
    included: query.get('included') === '1' && query.get('excluded') !== '1' ? '1' : '',
  };
  const wallets = useWallets();
  const cats = useCategories();
  const periods = useQuery<BudgetPeriod[]>('/periods?limit=24');
  const rates = useRates();
  const [search, setSearch] = useState(filters.q);
  const [searchOpen, setSearchOpen] = useState(!!filters.q);
  const [picker, setPicker] = useState<'wallet' | 'category' | 'range' | null>(null);
  const keyOf = (r: Range) => qs({ ...filters, from: r?.from ?? '', to: r?.to ?? '' });
  const key = keyOf(range);
  // the first page of every range is a cache entry of its own (kept on the device too): switching periods
  // paints that period's rows and totals at once and never shows another period's rows while loading
  const first = useQuery<TxPage>(firstPage(key));
  const [more, setMore] = useState<{ key: string; items: Transaction[]; hasMore: boolean; busy: boolean; error: Error | null } | null>(null);
  const extra = more?.key === key ? more : null;
  const items = useMemo(() => [...(first.data?.items ?? []), ...(extra?.items ?? [])], [first.data, extra]);
  const hasMore = extra ? extra.hasMore : !!first.data?.hasMore;
  const loaded = first.data !== undefined;
  const loading = !loaded && first.loading;
  const error = extra?.error ?? first.error ?? null;
  const [edit, setEdit] = useState<'new' | null>(null);
  const [open, setOpen] = useState<Transaction | null>(null);
  // by day (default) or by category, remembered on the device like Money Lover's view mode
  const [view, setViewState] = useState<'day' | 'category'>(() => { try { return localStorage.getItem('fin.txView') === 'category' ? 'category' : 'day'; } catch { return 'day'; } });
  const setView = (v: 'day' | 'category') => { setViewState(v); try { localStorage.setItem('fin.txView', v); } catch { /* storage unavailable */ } };

  useEffect(() => {
    const t = setTimeout(() => { if (search !== filters.q) setQuery({ q: search }); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // a refreshed first page makes pages loaded after it stale
  useEffect(() => { setMore(null); }, [first.data]);
  const loadMore = async () => {
    const k = key, offset = items.length;
    setMore((m) => ({ key: k, items: m?.key === k ? m.items : [], hasMore: true, busy: true, error: null }));
    try {
      const res = await get<TxPage>(`/transactions${k}${k ? '&' : '?'}limit=${PAGE}&offset=${offset}`);
      setMore((m) => (m?.key === k ? { ...m, items: [...m.items, ...res.items], hasMore: res.hasMore, busy: false } : m));
    } catch (e) {
      setMore((m) => (m?.key === k ? { ...m, busy: false, error: e as Error } : m));
    }
  };
  const reload = () => { setMore(null); void first.reload(); };

  const groups = useMemo(() => {
    const m = new Map<string, Transaction[]>();
    for (const t of filters.walletId ? items : dedupeTransfers(items)) m.set(t.date, [...(m.get(t.date) ?? [])!, t]);
    return [...m];
  }, [items, filters.walletId]);

  const dayTotal = (list: Transaction[]) => list.reduce((s, t) =>
    t.transferId || t.excludeFromReport || (t.categoryKind !== 'expense' && t.categoryKind !== 'income') ? s : s + (rates.toBase(ownPart(t), t.currency) ?? 0), 0);
  const rangeTotals = useMemo(() => {
    let inc = 0, exp = 0;
    for (const t of items) {
      if (t.transferId || t.excludeFromReport || (t.categoryKind !== 'expense' && t.categoryKind !== 'income')) continue;
      const v = rates.toBase(ownPart(t), t.currency) ?? 0;
      if (v > 0) inc += v; else exp -= v;
    }
    return { inc, exp };
  }, [items, rates.map]);

  // what kind of range is shown decides what the tabs next to it are
  const wholeMonth = !!range && range.from === monthStart(monthOf(range.from)) && range.to === monthEnd(monthOf(range.from));
  const period = range ? periods.data?.find((p) => p.startDate === range.from && p.endDate === range.to) : undefined;
  const titleOf = (r: Range): string => {
    if (!r) return 'Всё время';
    // one day, opened from the period ruler
    if (r.from === r.to) return r.from === today ? 'Сегодня' : r.from === addDays(today, -1) ? 'Вчера' : `${dateLong(r.from)}, ${weekday(r.from, true)}`;
    const isMonth = r.from === monthStart(monthOf(r.from)) && r.to === monthEnd(monthOf(r.from));
    if (isMonth) {
      const ym = monthOf(r.from), now = monthOf(today);
      return ym === now ? 'Этот месяц' : ym === shiftMonth(now, -1) ? 'Прошлый месяц' : ym === shiftMonth(now, 1) ? 'Следующий месяц' : monthTitle(ym);
    }
    const p = periods.data?.find((x) => x.startDate === r.from && x.endDate === r.to);
    if (p) return p.startDate <= today && p.endDate >= today ? 'Этот период' : rangeLabel(p.startDate, p.endDate);
    return rangeLabel(r.from, r.to);
  };
  const shifted = (dir: -1 | 1): Range | undefined => {
    if (!range) return undefined;
    if (wholeMonth) {
      const ym = shiftMonth(monthOf(range.from), dir);
      return { from: monthStart(ym), to: monthEnd(ym) };
    }
    if (period && periods.data) {
      const next = periods.data[periods.data.findIndex((p) => p.id === period.id) - dir]; // the list is newest first
      return next ? { from: next.startDate, to: next.endDate } : undefined;
    }
    const len = Math.round((parseIso(range.to).getTime() - parseIso(range.from).getTime()) / 86400000) + 1;
    return { from: addDays(range.from, dir * len), to: addDays(range.to, dir * len) };
  };
  const [slide, setSlide] = useState<'prev' | 'next' | null>(null);
  const go = (r: Range | undefined, dir?: 'prev' | 'next') => {
    if (!r) return;
    setSlide(dir ?? null);
    setQuery({ from: r.from, to: r.to, all: null });
  };
  const prev = shifted(-1), next = shifted(1);
  const rangeTitle = titleOf(range);
  // the neighbouring periods are loaded in the background, so the next tap or swipe paints at once
  useEffect(() => {
    if (!loaded) return;
    for (const r of [prev, next]) if (r) prefetch(firstPage(keyOf(r)));
  }, [key, loaded, prev?.from, prev?.to, next?.from, next?.to]);
  // a horizontal swipe flips periods like the tabs above the list
  useSwipe((dir) => go(dir === 'left' ? next : prev, dir === 'left' ? 'next' : 'prev'));

  const wallet = wallets.data?.find((w) => w.id === Number(filters.walletId));
  const category = cats.data?.find((c) => c.id === Number(filters.categoryId));
  const hasFilters = !!(filters.categoryId || filters.q || filters.kind || filters.excluded || filters.included);
  // «Итого» as in Money Lover: every wallet counted in the total, in the base currency
  const balance = wallet ? { value: wallet.balance, currency: wallet.currency }
    : { value: (wallets.data ?? []).filter((w) => !w.archived && !w.excludeFromTotal).reduce((s, w) => s + (w.balanceBase ?? 0), 0), currency: rates.base };
  const net = rangeTotals.inc - rangeTotals.exp;
  const shownItems = filters.walletId ? items : dedupeTransfers(items);
  const reportLink = range ? `/reports${qs({ from: range.from, to: range.to, wallet: filters.walletId || undefined })}` : '/reports';

  return (
    <div className="page">
      <PageHead title="Операции" actions={<Button variant="primary" className="hide-mobile" onClick={() => setEdit('new')}><Icon name="plus" /> Добавить</Button>} />
      <div className="ledger-top">
        <div className="ledger-wallet">
          <button type="button" className="wallet-switch" onClick={() => setPicker('wallet')} aria-haspopup="dialog" aria-label={`Кошелёк: ${wallet?.name ?? 'все'}, выбрать`}>
            {wallet ? <Tile icon={walletIcon(wallet)} color={wallet.color} size="sm" /> : <Tile icon="wallet" color="#8A8F98" size="sm" />}
            <span>{wallet?.name ?? 'Итого'}</span>
            <Icon name="down" size={16} />
          </button>
          <span className="ledger-balance">
            <span className="muted small">Баланс</span>
            <span className={cx('hero-mid money', balance.value < 0 && 'neg')}>{money(balance.value, balance.currency)}</span>
          </span>
        </div>
        <div className="ledger-actions">
          <button type="button" className={cx('icon-btn icon-btn-framed', searchOpen && 'on')} aria-label="Поиск" aria-expanded={searchOpen}
            onClick={() => { if (searchOpen) { setSearch(''); setQuery({ q: null }); } setSearchOpen((v) => !v); }}>
            <Icon name={searchOpen ? 'close' : 'search'} />
          </button>
          <button type="button" className="icon-btn icon-btn-framed" aria-pressed={view === 'category'}
            aria-label={view === 'category' ? 'Показать по операциям' : 'Показать по категориям'} onClick={() => setView(view === 'category' ? 'day' : 'category')}>
            <span className="swap" key={view}><Icon name={view === 'category' ? 'list' : 'folder'} /></span>
          </button>
        </div>
      </div>

      <nav className="range-tabs" aria-label="Период">
        <button type="button" className="range-tab" disabled={!prev} onClick={() => go(prev, 'prev')}><span>{prev ? titleOf(prev) : ''}</span></button>
        <button type="button" className="range-tab on" onClick={() => setPicker('range')} aria-haspopup="dialog" aria-label={`${rangeTitle}, выбрать период`}>
          <span>{rangeTitle}</span><Icon name="calendar" size={16} />
        </button>
        <button type="button" className="range-tab" disabled={!next} onClick={() => go(next, 'next')}><span>{next ? titleOf(next) : ''}</span></button>
      </nav>

      <div className="chips-row">
          {searchOpen ? <Input type="search" placeholder="Заметка, категория, контакт" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Поиск по операциям" autoFocus /> : null}
          <button type="button" className={cx('chip-btn', category && 'on')} onClick={() => setPicker('category')}>
            {category ? <Tile icon={categoryIcon(category)} color={categoryColor(category)} size="sm" image={category.iconImage} /> : <Icon name="folder" size={16} />}
            <span>{category ? categoryLabel(category, cats.data ?? []) : 'Все категории'}</span>
          </button>
          <button type="button" className={cx('chip-btn', filters.excluded && 'on')} aria-pressed={!!filters.excluded} onClick={() => setQuery({ excluded: filters.excluded ? null : 1, included: null })}>Не в отчётах</button>
          {filters.included ? <button type="button" className="chip-btn on" onClick={() => setQuery({ included: null })}>Учитываются в отчётах <Icon name="close" size={16} /></button> : null}
          {filters.kind ? <button type="button" className="chip-btn on" onClick={() => setQuery({ kind: null })}>{kindLabels[filters.kind]} <Icon name="close" size={16} /></button> : null}
          {hasFilters ? (
            <button type="button" className="chip-btn" onClick={() => { setSearch(''); setSearchOpen(false); setQuery({ category: null, q: null, kind: null, excluded: null, included: null }); }}>
              <Icon name="close" size={16} /><span>Сбросить</span>
            </button>
          ) : null}
      </div>

      <div className="period-view">
        <div key={key} className={cx('period-body', slide && `slide-${slide}`)}>
          {range && items.length ? (
            <section className="overview" aria-label="Обзор периода">
              <dl className="overview-rows">
                <div><dt>Доходы</dt><dd className="money pos">{rub(rangeTotals.inc, { sign: true })}</dd></div>
                <div><dt>Расходы</dt><dd className="money neg">{rub(-rangeTotals.exp, { sign: true })}</dd></div>
                <div className="overview-net"><dt className="sr-only">Итог</dt><dd className={cx('money', net < 0 && 'neg')}>{rub(net, { sign: true })}</dd></div>
              </dl>
              <Link to={reportLink} className="link overview-link">Отчёт за этот период <Icon name="right" size={16} /></Link>
              {hasMore ? <p className="muted small overview-note">Итоги по загруженным операциям — показаны не все.</p> : null}
            </section>
          ) : null}

          {/* offline with saved operations on screen: the offline bar already explains it */}
          {error && !(error instanceof NetworkError && items.length) ? <ErrorBox error={error} onRetry={extra?.error ? loadMore : reload} /> : null}
          {!items.length && loading ? <Loading /> : null}
          {!items.length && loaded && !error ? (
            <Empty action={range ? <Button size="sm" onClick={() => setPicker('range')}><Icon name="calendar" size={16} /> Другой период</Button> : undefined}>
              {range ? `${rangeTitle}: операций нет.` : 'Операций не найдено.'}
            </Empty>
          ) : null}

          {view === 'day' ? (
            <div className="days">
              {groups.map(([date, list]) => {
                const total = dayTotal(list);
                const d = parseIso(date);
                return (
                  <section key={date} className="day" aria-label={dateLong(date, true)}>
                    <header className="day-head">
                      <span className="day-num" aria-hidden>{d.getUTCDate()}</span>
                      <span className="day-when">
                        <strong>{date === today ? 'Сегодня' : date === addDays(today, -1) ? 'Вчера' : weekday(date)}</strong>
                        <span>{monthName(d.getUTCMonth())} {d.getUTCFullYear()}</span>
                      </span>
                      {total ? <span className={cx('money day-total', total < 0 ? 'neg' : 'pos')}>{rub(total, { sign: true })}</span> : <span />}
                    </header>
                    <ul className="tx-list">
                      {list.map((t) => <TxRow key={t.id} t={t} onClick={() => setOpen(t)} />)}
                    </ul>
                  </section>
                );
              })}
            </div>
          ) : (
            <ByCategory list={shownItems} onOpen={setOpen} toBase={rates.toBase} />
          )}
          {hasMore ? <div className="center"><Button busy={!!extra?.busy} onClick={() => void loadMore()}>Показать ещё</Button></div> : null}
        </div>
      </div>
      {open ? <TxDetail tx={open} onClose={() => { setOpen(null); reload(); }} /> : null}
      {edit ? <TransactionModal defaults={{ walletId: filters.walletId ? Number(filters.walletId) : undefined }}
        onClose={() => { setEdit(null); reload(); }} /> : null}
      {picker === 'wallet' ? (
        <WalletPicker title="Кошелёк" value={wallet?.id ?? null} allLabel="Итого — все кошельки" onClose={() => setPicker(null)}
          onPick={(id) => { setQuery({ wallet: id }); setPicker(null); }} onAll={() => { setQuery({ wallet: null }); setPicker(null); }} />
      ) : null}
      {picker === 'category' ? (
        <CategoryPicker value={category?.id ?? null} onClose={() => setPicker(null)}
          onPick={(id) => { setQuery({ category: id }); setPicker(null); }} onAll={() => { setQuery({ category: null }); setPicker(null); }} />
      ) : null}
      {picker === 'range' ? (
        <RangePicker range={range} periods={periods.data ?? []} onClose={() => setPicker(null)}
          onPick={(r) => { setQuery(r ? { from: r.from, to: r.to, all: null } : { from: null, to: null, all: 1 }); setPicker(null); }} />
      ) : null}
    </div>
  );
}

/**
 * Money Lover's «Сортировать по категориям»: one card per category with its total and count,
 * the operations under it with their date. Transfers are kept apart at the end.
 */
function ByCategory({ list, onOpen, toBase }: { list: Transaction[]; onOpen: (t: Transaction) => void; toBase: (v: number, c: string) => number | null }) {
  const cats = useCategories();
  const groups = useMemo(() => {
    const m = new Map<string, { key: string; first: Transaction; list: Transaction[]; total: number }>();
    for (const t of list) {
      const key = t.transferId ? 'transfer' : String(t.categoryId);
      const g = m.get(key) ?? { key, first: t, list: [], total: 0 };
      g.list.push(t);
      if (!t.transferId && !t.excludeFromReport) g.total += toBase(ownPart(t), t.currency) ?? 0;
      m.set(key, g);
    }
    return [...m.values()].sort((a, b) => (a.key === 'transfer' ? 1 : b.key === 'transfer' ? -1 : Math.abs(b.total) - Math.abs(a.total)));
  }, [list, toBase]);
  if (!groups.length) return null;
  return (
    <div className="days">
      {groups.map((g) => {
        const t = g.first;
        const cat = cats.data?.find((c) => c.id === t.categoryId);
        const isTransfer = g.key === 'transfer';
        const name = isTransfer ? 'Переводы' : cat ? categoryLabel(cat, cats.data ?? []) : t.categoryName;
        return (
          <section key={g.key} className="day" aria-label={name}>
            <header className="day-head cat-head">
              {isTransfer ? <Tile icon="transfer" color={kindTint.transfer} />
                : <Tile icon={categoryIcon(cat ?? { name: t.categoryName, kind: t.categoryKind })} color={categoryColor(cat ?? { name: t.categoryName, kind: t.categoryKind })} image={cat?.iconImage} />}
              <span className="day-when">
                <strong>{name}</strong>
                <span>{g.list.length} {plural(g.list.length, 'операция', 'операции', 'операций')}</span>
              </span>
              {!isTransfer && g.total ? <span className={cx('money day-total', g.total < 0 ? 'neg' : 'pos')}>{rub(g.total, { sign: true })}</span> : <span />}
            </header>
            <ul className="tx-list">
              {g.list.map((x) => (
                <li key={x.id}>
                  <button type="button" className="tx tx-dated" onClick={() => onOpen(x)}>
                    <span className="tx-date" aria-hidden><span className="day-num">{parseIso(x.date).getUTCDate()}</span></span>
                    <span className="tx-text">
                      <span className="tx-title">{x.note || x.walletName}</span>
                      <span className="tx-sub">{[weekday(x.date), x.note ? x.walletName : null, x.contactName].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="tx-right">
                      <span className={cx('money', x.transferId ? 'muted' : x.amount < 0 ? 'neg' : 'pos')}>{money(x.amount, x.currency, { sign: !x.transferId })}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function RangePicker({ range, periods, onPick, onClose }: { range: Range; periods: BudgetPeriod[]; onPick: (r: Range) => void; onClose: () => void }) {
  const today = todayIso();
  const ym = monthOf(today);
  const [from, setFrom] = useState(range?.from ?? monthStart(ym));
  const [to, setTo] = useState(range?.to ?? today);
  const is = (r: Range) => (range === null && r === null) || (!!range && !!r && range.from === r.from && range.to === r.to);
  const options: { label: string; sub?: string; r: Range }[] = [
    { label: 'Этот месяц', sub: monthTitle(ym), r: { from: monthStart(ym), to: monthEnd(ym) } },
    { label: 'Прошлый месяц', sub: monthTitle(shiftMonth(ym, -1)), r: { from: monthStart(shiftMonth(ym, -1)), to: monthEnd(shiftMonth(ym, -1)) } },
    { label: 'Последние 30 дней', sub: `${dateShort(addDays(today, -29))} – ${dateShort(today)}`, r: { from: addDays(today, -29), to: today } },
    { label: 'Этот год', sub: today.slice(0, 4), r: { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` } },
    { label: 'Всё время', r: null },
  ];
  const row = (label: string, sub: string | undefined, r: Range) => (
    <button key={label} type="button" className={cx('picker-row', is(r) && 'on')} onClick={() => onPick(r)} aria-pressed={is(r)}>
      <span className="picker-row-text"><span>{label}</span>{sub ? <span className="muted">{sub}</span> : null}</span>
      {is(r) ? <Icon name="check" size={18} /> : <span />}
    </button>
  );
  return (
    <Modal title="Период" onClose={onClose}>
      <div className="range-options">
        {options.map((o) => row(o.label, o.sub, o.r))}
        {periods.length ? <div className="picker-group">Бюджетные периоды</div> : null}
        {periods.slice(0, 8).map((p) => row(rangeLabel(p.startDate, p.endDate), p.startDate <= today && p.endDate >= today ? 'текущий' : undefined, { from: p.startDate, to: p.endDate }))}
      </div>
      <div className="range-custom">
        <span className="picker-group" style={{ padding: 0 }}>Произвольные даты</span>
        <div className="form-row">
          <Field label="С"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="По"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </div>
        <Button size="sm" variant="primary" disabled={!from || !to || from > to} onClick={() => onPick({ from, to })}>Показать</Button>
      </div>
    </Modal>
  );
}
